import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiFetch, clinicDataApi, reportsApi } from '../services/api';
import { Chart, registerables } from 'chart.js';
import html2canvas from 'html2canvas';
import ClinicDataSection from '../components/ClinicDataSection';
import LeadGoalsBlock from '../components/reports/LeadGoalsBlock';
import LeadGoalProgress from '../components/reports/LeadGoalProgress';
Chart.register(...registerables);

const OBJECTIVE_OPTIONS = [
  { value: 'ALL', label: 'Todos os Objetivos' },
  { value: 'LEAD_GENERATION', label: 'Geração de Leads' },
  { value: 'CONVERSIONS', label: 'Conversões' },
  { value: 'MESSAGES', label: 'Conversas Iniciadas (Mensagens)' },
  { value: 'TRAFFIC', label: 'Tráfego' },
  { value: 'AWARENESS', label: 'Reconhecimento' },
  { value: 'VIDEO_VIEWS', label: 'Visualizações de Vídeo' },
];

const DATE_PRESETS = [
  { value: 'last_7d', label: 'Últimos 7 dias' },
  { value: 'last_14d', label: 'Últimos 14 dias' },
  { value: 'last_30d', label: 'Últimos 30 dias' },
  { value: 'current_month', label: 'Mês Atual' },
  { value: 'custom', label: 'Personalizado' },
];

const fmt = (n, dec = 2) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };
const fmtCurrency = (n) => `R$ ${fmt(n)}`;
const fmtPct = (n) => `${fmt(Number(n || 0), 2)}%`;

const SOURCE_CONFIG = {
  meta: { label: 'Meta', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  google: { label: 'Google', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
};

const metaIdOf = (acc) => acc.account_id || acc.metaAccountId || acc.id?.replace('act_', '');

// Objetivo do relatorio -> tipo de meta (MESSAGES->Conversas, LEAD_GENERATION->Leads, ALL/demais->Conversas)
const goalTypeForObjective = (obj) => (obj === 'LEAD_GENERATION' ? 'lead' : 'message');

const MAX_GOAL_UNITS = 50;

export default function CombinedReports() {
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState([]);
  const [selectedGoogle, setSelectedGoogle] = useState([]);
  const [datePreset, setDatePreset] = useState('last_30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [objective, setObjective] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [sortField, setSortField] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [reportPeriod, setReportPeriod] = useState({ startDate: '', endDate: '' });
  const [demographics, setDemographics] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [topCreatives, setTopCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [rankMetric, setRankMetric] = useState('conversions');
  const [exporting, setExporting] = useState(false);
  const [accountsStatus, setAccountsStatus] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState([]);
  const [showFunnelAndGoals, setShowFunnelAndGoals] = useState(true);
  const [leadGoal, setLeadGoal] = useState(null);
  const [leadGoalAchieved, setLeadGoalAchieved] = useState(0);
  const reportRef = useRef(null);
  const ageChartRef = useRef(null);
  const genderChartRef = useRef(null);
  const ageChartInstance = useRef(null);
  const genderChartInstance = useRef(null);

  // Dropdown states
  const [metaDropdownOpen, setMetaDropdownOpen] = useState(false);
  const [googleDropdownOpen, setGoogleDropdownOpen] = useState(false);
  const [metaSearch, setMetaSearch] = useState('');
  const [googleSearch, setGoogleSearch] = useState('');

  // Unidades mapeadas (chips) — uniao dedupe em selectedMeta/selectedGoogle
  const [units, setUnits] = useState([]);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [unitSearch, setUnitSearch] = useState('');
  // accountId -> unitIds que o adicionaram; so contas que entraram "via unidade" (nao manuais)
  const [unitSources, setUnitSources] = useState({ meta: {}, google: {} });

  // Metas do mes (independente do periodo do relatorio)
  const [goalsType, setGoalsType] = useState('message');
  const [goalsData, setGoalsData] = useState(null);
  const [goalsStatus, setGoalsStatus] = useState('idle'); // idle | loading | ready | error
  const [goalsUnmapped, setGoalsUnmapped] = useState(0);
  const [goalsIncomplete, setGoalsIncomplete] = useState(0);
  const [goalsExpected, setGoalsExpected] = useState(0);
  const [goalsOverLimit, setGoalsOverLimit] = useState(0);
  const goalsReqRef = useRef(0);
  const goalsPromiseRef = useRef(null);
  const goalsUnitIdsRef = useRef([]);

  const getDateRange = () => {
    if (datePreset === 'custom') return { startDate, endDate };
    const today = new Date();
    if (datePreset === 'current_month') {
      return {
        startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    }
    const days = datePreset === 'last_7d' ? 7 : datePreset === 'last_14d' ? 14 : 30;
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const loadAccounts = useCallback(async () => {
    try {
      const [metaResp, googleResp, unitsResp] = await Promise.allSettled([
        apiFetch('/meta/accounts'),
        apiFetch('/google-ads/accounts'),
        reportsApi.getLeadGoalUnits(),
      ]);
      if (metaResp.status === 'fulfilled') {
        setMetaAccounts(metaResp.value.data?.accounts || []);
      }
      if (googleResp.status === 'fulfilled') {
        setGoogleAccounts(googleResp.value.data?.accounts || []);
      }
      if (unitsResp.status === 'fulfilled') {
        setUnits(unitsResp.value?.data?.units || []);
        setUnitsLoaded(true);
      }
    } catch {}
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Contas de cada unidade, restritas as que existem nas listas carregadas (ids Meta crus; Google por customerId)
  const unitAccountsMap = useMemo(() => {
    const knownMeta = new Set(metaAccounts.map(metaIdOf));
    const out = {};
    units.forEach((u) => {
      out[u.id] = {
        meta: [...new Set((u.metaAccountIds || []).map((id) => String(id).replace(/^act_/, '')))].filter((id) => knownMeta.has(id)),
        google: [...new Set((u.googleAccountIds || [])
          .map((id) => googleAccounts.find((a) => a.customerId === id || a.id === id)?.customerId)
          .filter(Boolean))],
      };
    });
    return out;
  }, [units, metaAccounts, googleAccounts]);

  const accountsReady = metaAccounts.length > 0 || googleAccounts.length > 0;

  const applyUnitSelection = (nextIds) => {
    let meta = [...selectedMeta];
    let google = [...selectedGoogle];
    const src = { meta: { ...unitSources.meta }, google: { ...unitSources.google } };
    const removed = selectedUnits.filter((id) => !nextIds.includes(id));
    const added = nextIds.filter((id) => !selectedUnits.includes(id));

    const drop = (kind, id, uid) => {
      if (!src[kind][id]) return; // selecionada manualmente: nunca removida por chip
      const rest = src[kind][id].filter((x) => x !== uid);
      if (rest.length) { src[kind][id] = rest; return; }
      delete src[kind][id];
      if (kind === 'meta') meta = meta.filter((x) => x !== id); else google = google.filter((x) => x !== id);
    };
    const add = (kind, id, uid) => {
      const list = kind === 'meta' ? meta : google;
      if (!list.includes(id)) {
        list.push(id);
        src[kind][id] = [uid];
      } else if (src[kind][id] && !src[kind][id].includes(uid)) {
        src[kind][id] = [...src[kind][id], uid];
      }
    };

    removed.forEach((uid) => {
      const a = unitAccountsMap[uid];
      if (!a) return;
      a.meta.forEach((id) => drop('meta', id, uid));
      a.google.forEach((id) => drop('google', id, uid));
    });
    added.forEach((uid) => {
      const a = unitAccountsMap[uid];
      if (!a) return;
      a.meta.forEach((id) => add('meta', id, uid));
      a.google.forEach((id) => add('google', id, uid));
    });

    setSelectedMeta(meta);
    setSelectedGoogle(google);
    setUnitSources(src);
    setSelectedUnits(nextIds);
  };

  const toggleUnit = (id) => applyUnitSelection(selectedUnits.includes(id) ? selectedUnits.filter((x) => x !== id) : [...selectedUnits, id]);

  const dropSource = (kind, id) => setUnitSources((s) => {
    if (!s[kind][id]) return s;
    const next = { ...s[kind] };
    delete next[id];
    return { ...s, [kind]: next };
  });

  // Marcacao manual de uma conta tira o "via unidade" dela
  const toggleMeta = (id) => {
    dropSource('meta', id);
    setSelectedMeta((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };
  const toggleGoogle = (id) => {
    dropSource('google', id);
    setSelectedGoogle((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  };

  const selectAllIn = (kind, ids) => {
    const setter = kind === 'meta' ? setSelectedMeta : setSelectedGoogle;
    setter((p) => [...new Set([...p, ...ids])]);
    setUnitSources((s) => {
      const next = { ...s[kind] };
      ids.forEach((id) => { delete next[id]; });
      return { ...s, [kind]: next };
    });
  };
  // Limpar um dropdown: zera so as contas desse tipo e as fontes dele. Unidades que tinham contas
  // desse tipo saem da selecao (o chip deixaria de refletir a uniao) e deixam de "possuir" contas
  // do outro tipo, que ficam selecionadas como manuais — desmarcar chips depois remove so o certo.
  const clearIn = (kind) => {
    const other = kind === 'meta' ? 'google' : 'meta';
    const dropped = selectedUnits.filter((uid) => (unitAccountsMap[uid]?.[kind] || []).length > 0);
    (kind === 'meta' ? setSelectedMeta : setSelectedGoogle)([]);
    setUnitSources((s) => {
      const otherSrc = {};
      Object.entries(s[other]).forEach(([id, uids]) => {
        const rest = uids.filter((u) => !dropped.includes(u));
        if (rest.length) otherSrc[id] = rest; // conta sem dono vira manual (sai do mapa, segue selecionada)
      });
      return { [kind]: {}, [other]: otherSrc };
    });
    setSelectedUnits((p) => p.filter((uid) => !dropped.includes(uid)));
  };

  const filteredMeta = useMemo(() => {
    const q = metaSearch.trim().toLowerCase();
    return q ? metaAccounts.filter((a) => (a.name || '').toLowerCase().includes(q) || String(metaIdOf(a)).includes(q)) : metaAccounts;
  }, [metaAccounts, metaSearch]);

  const filteredGoogle = useMemo(() => {
    const q = googleSearch.trim().toLowerCase();
    const list = googleAccounts.filter((a) => !a.isManager);
    return q ? list.filter((a) => (a.name || '').toLowerCase().includes(q) || String(a.customerId).includes(q)) : list;
  }, [googleAccounts, googleSearch]);

  const filteredUnits = useMemo(() => {
    const q = unitSearch.trim().toLowerCase();
    return q ? units.filter((u) => u.name.toLowerCase().includes(q)) : units;
  }, [units, unitSearch]);

  const unitSummary = useMemo(() => {
    const meta = new Set();
    const google = new Set();
    selectedUnits.forEach((uid) => {
      unitAccountsMap[uid]?.meta.forEach((id) => meta.add(id));
      unitAccountsMap[uid]?.google.forEach((id) => google.add(id));
    });
    return { meta: meta.size, google: google.size };
  }, [selectedUnits, unitAccountsMap]);

  // Unidades que ganham card: chips marcados + unidades cujas contas Meta estao todas selecionadas
  const computeGoalUnitIds = () => {
    const ids = new Set(selectedUnits);
    units.forEach((u) => {
      const m = unitAccountsMap[u.id]?.meta || [];
      if (m.length > 0 && m.every((id) => selectedMeta.includes(id))) ids.add(u.id);
    });
    return units.filter((u) => ids.has(u.id)).map((u) => u.id);
  };

  // Contas selecionadas de unidades que nao ganharam card (unidade so parcialmente marcada)
  const computeIncompleteCount = (goalUnitIds) => {
    const mapped = new Set(units.flatMap((u) => unitAccountsMap[u.id]?.meta || []));
    const covered = new Set(goalUnitIds.flatMap((uid) => unitAccountsMap[uid]?.meta || []));
    return selectedMeta.filter((id) => mapped.has(id) && !covered.has(id)).length;
  };

  const computeUnmappedCount = () => {
    if (!unitsLoaded) return 0;
    const mapped = new Set(units.flatMap((u) => (u.metaAccountIds || []).map((id) => String(id).replace(/^act_/, ''))));
    return selectedMeta.filter((id) => !mapped.has(id)).length;
  };

  const loadGoals = useCallback((type, unitIds) => {
    const reqId = ++goalsReqRef.current;
    if (!unitIds.length) {
      setGoalsData(null);
      setGoalsStatus('idle');
      goalsPromiseRef.current = Promise.resolve(null);
      return goalsPromiseRef.current;
    }
    setGoalsStatus('loading');
    const p = reportsApi.getLeadGoals({ objective: type, unitIds: unitIds.slice(0, MAX_GOAL_UNITS) })
      .then((res) => {
        if (reqId !== goalsReqRef.current) return null;
        setGoalsData(res?.data || null);
        setGoalsStatus(res?.data ? 'ready' : 'error');
        return res?.data ? { type, data: res.data } : null;
      })
      .catch(() => {
        if (reqId === goalsReqRef.current) setGoalsStatus('error');
        return null;
      });
    goalsPromiseRef.current = p;
    return p;
  }, []);

  const handleGoalsTypeChange = (type) => {
    setGoalsType(type);
    loadGoals(type, goalsUnitIdsRef.current);
  };

  const fetchDemographics = useCallback(async (sd, ed, obj, metaIds) => {
    if (!metaIds.length) return;
    setDemoLoading(true);
    setDemoError('');
    try {
      const params = new URLSearchParams();
      params.set('metaAccountIds', metaIds.join(','));
      params.set('startDate', sd);
      params.set('endDate', ed);
      if (obj !== 'ALL') params.set('objective', obj);
      const resp = await apiFetch(`/reports/combined/demographics?${params.toString()}`);
      setDemographics(resp.data || null);
    } catch (err) {
      setDemoError(err.message || 'Erro ao buscar dados demográficos');
    } finally {
      setDemoLoading(false);
    }
  }, []);

  const fetchTopCreatives = useCallback(async (sd, ed, obj, metaIds) => {
    if (!metaIds.length) return;
    setCreativesLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('metaAccountIds', metaIds.join(','));
      params.set('startDate', sd);
      params.set('endDate', ed);
      if (obj !== 'ALL') params.set('objective', obj);
      params.set('limit', '18');
      const resp = await apiFetch(`/reports/combined/top-creatives?${params.toString()}`);
      setTopCreatives(resp.data || []);
    } catch {
      setTopCreatives([]);
    } finally {
      setCreativesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!demographics || !ageChartRef.current || !genderChartRef.current) return;
    const { byAge, byGender } = demographics;

    if (ageChartInstance.current) ageChartInstance.current.destroy();
    if (genderChartInstance.current) genderChartInstance.current.destroy();

    const GENDER_LABELS = { male: 'Masculino', female: 'Feminino', unknown: 'Não informado' };
    const GENDER_COLORS = { male: '#3b82f6', female: '#ec4899', unknown: '#94a3b8' };

    ageChartInstance.current = new Chart(ageChartRef.current, {
      type: 'bar',
      data: {
        labels: byAge.map(r => r.age),
        datasets: [
          {
            label: 'Conversões',
            data: byAge.map(r => r.conversions),
            backgroundColor: 'rgba(205,25,249,0.75)',
            borderRadius: 6,
            yAxisID: 'y',
          },
          {
            label: 'Cliques',
            data: byAge.map(r => r.clicks),
            backgroundColor: 'rgba(99,102,241,0.35)',
            borderRadius: 6,
            yAxisID: 'y1',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { font: { size: 11 } } }, tooltip: { mode: 'index' } },
        scales: {
          y: { position: 'left', title: { display: true, text: 'Conversões' }, beginAtZero: true },
          y1: { position: 'right', title: { display: true, text: 'Cliques' }, beginAtZero: true, grid: { drawOnChartArea: false } },
        },
      },
    });

    genderChartInstance.current = new Chart(genderChartRef.current, {
      type: 'doughnut',
      data: {
        labels: byGender.map(r => GENDER_LABELS[r.gender] || r.gender),
        datasets: [{
          data: byGender.map(r => r.conversions),
          backgroundColor: byGender.map(r => GENDER_COLORS[r.gender] || '#94a3b8'),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                return ` ${ctx.label}: ${ctx.parsed.toFixed(1)} (${pct}%)`;
              }
            }
          }
        },
        cutout: '65%',
      },
    });

    return () => {
      ageChartInstance.current?.destroy();
      genderChartInstance.current?.destroy();
    };
  }, [demographics]);

  const handleGenerate = async () => {
    if (selectedMeta.length === 0 && selectedGoogle.length === 0) {
      setError('Selecione ao menos uma conta Meta ou Google Ads');
      return;
    }
    setLoading(true);
    setError('');
    setData([]);
    setSummary(null);
    setDemographics(null);
    setTopCreatives([]);
    setAccountsStatus([]);

    // Build pending list for progress display
    const pending = [
      ...selectedMeta.map(id => {
        const acc = metaAccounts.find(a => (a.account_id || a.metaAccountId || a.id?.replace('act_', '')) === id);
        return { source: 'meta', accountId: id, accountName: acc?.name || id, status: 'loading' };
      }),
      ...selectedGoogle.map(id => {
        const acc = googleAccounts.find(a => a.customerId === id);
        return { source: 'google', accountId: id, accountName: acc?.name || id, status: 'loading' };
      }),
    ];
    setLoadingAccounts(pending);

    // Metas do mes em paralelo ao relatorio (1 request em lote; independe do periodo)
    const nextGoalsType = goalTypeForObjective(objective);
    const goalUnitIds = computeGoalUnitIds();
    goalsUnitIdsRef.current = goalUnitIds.slice(0, MAX_GOAL_UNITS);
    setGoalsType(nextGoalsType);
    setGoalsData(null);
    setGoalsUnmapped(computeUnmappedCount());
    setGoalsIncomplete(computeIncompleteCount(goalUnitIds));
    setGoalsExpected(goalsUnitIdsRef.current.length);
    setGoalsOverLimit(Math.max(0, goalUnitIds.length - MAX_GOAL_UNITS));
    loadGoals(nextGoalsType, goalsUnitIdsRef.current);

    try {
      const { startDate: sd, endDate: ed } = getDateRange();
      const params = new URLSearchParams();
      if (selectedMeta.length) params.set('metaAccountIds', selectedMeta.join(','));
      if (selectedGoogle.length) params.set('googleCustomerIds', selectedGoogle.join(','));
      params.set('startDate', sd);
      params.set('endDate', ed);
      if (objective !== 'ALL') params.set('objective', objective);

      const resp = await apiFetch(`/reports/combined?${params.toString()}`);
      setData(resp.data || []);
      setSummary(resp.summary || null);
      setAccountsStatus(resp.accountsStatus || []);
      setReportPeriod({ startDate: sd, endDate: ed });
      setIsVisualizing(true);

      if (selectedMeta.length > 0) {
        fetchDemographics(sd, ed, objective, selectedMeta);
        fetchTopCreatives(sd, ed, objective, selectedMeta);
        if (showFunnelAndGoals) {
          const now = new Date();
          const curYear = now.getFullYear();
          const curMonth = now.getMonth() + 1;
          reportsApi.getLeadGoal(selectedMeta[0], curYear, curMonth)
            .then(res => {
              if (res?.data && res.data.state === 'ok') {
                setLeadGoal(res.data);
                if (objective === 'MESSAGES') {
                  setLeadGoalAchieved(resp.summary?.totalConversions || 0);
                } else {
                  setLeadGoalAchieved(Number(res.data.clinicLeadsMonth || 0));
                }
              } else {
                setLeadGoal(null);
                setLeadGoalAchieved(0);
              }
            })
            .catch(() => {
              setLeadGoal(null);
              setLeadGoalAchieved(0);
            });
        } else {
          setLeadGoal(null);
          setLeadGoalAchieved(0);
        }
      } else {
        setLeadGoal(null);
        setLeadGoalAchieved(0);
      }
    } catch (err) {
      setError(err.message || 'Erro ao gerar relatório combinado');
    } finally {
      setLoading(false);
      setLoadingAccounts([]);
    }
  };

  const handleShare = async () => {
    try {
      const { startDate: sd, endDate: ed } = getDateRange();

      // If async sections are still loading, wait for them before sharing
      let demoSnapshot = demographics;
      let creativesSnapshot = topCreatives;
      if ((demoLoading || creativesLoading) && selectedMeta.length > 0) {
        const [demoResult, creativesResult] = await Promise.allSettled([
          demoLoading
            ? fetch(`/api/reports/combined/demographics?metaAccountIds=${selectedMeta.join(',')}&startDate=${sd}&endDate=${ed}${objective !== 'ALL' ? `&objective=${objective}` : ''}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              }).then(r => r.json()).then(r => r.data || null)
            : Promise.resolve(demographics),
          creativesLoading
            ? fetch(`/api/reports/combined/top-creatives?metaAccountIds=${selectedMeta.join(',')}&startDate=${sd}&endDate=${ed}${objective !== 'ALL' ? `&objective=${objective}` : ''}&limit=18`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              }).then(r => r.json()).then(r => r.data || [])
            : Promise.resolve(topCreatives),
        ]);
        demoSnapshot = demoResult.status === 'fulfilled' ? demoResult.value : demographics;
        creativesSnapshot = creativesResult.status === 'fulfilled' ? creativesResult.value : topCreatives;
      }

      // Snapshot clinic data KPIs too, mirroring the live section rendered below
      // (see clinicDbIds usage further down) — shared link is a static snapshot.
      const clinicDbIds = selectedMeta
        .map(id => metaAccounts.find(a => (a.account_id || a.metaAccountId || a.id?.replace('act_', '')) === id)?.id)
        .filter(Boolean);
      let clinicSnapshot = null;
      if (clinicDbIds.length > 0) {
        clinicSnapshot = await clinicDataApi.getAccountData(clinicDbIds, sd, ed)
          .then(res => res.data || [])
          .catch(() => null);
      }

      // Metas do mes: congela o que esta na tela (aguarda se ainda carregando)
      let goalsSnapshot = null;
      if (goalsStatus === 'loading') {
        const pending = await goalsPromiseRef.current;
        if (pending) goalsSnapshot = { type: pending.type, data: pending.data, unmappedAccounts: goalsUnmapped };
      } else if (goalsStatus === 'ready' && goalsData) {
        goalsSnapshot = { type: goalsType, data: goalsData, unmappedAccounts: goalsUnmapped };
      }

      const resp = await apiFetch('/reports/combined/share', {
        method: 'POST',
        body: JSON.stringify({
          metaAccountIds: selectedMeta,
          googleCustomerIds: selectedGoogle,
          startDate: sd,
          endDate: ed,
          objective,
          name: 'Relatório Combinado',
          snapshotData: data,
          snapshotSummary: summary,
          snapshotDemographics: demoSnapshot,
          snapshotTopCreatives: creativesSnapshot,
          snapshotAccountsStatus: accountsStatus,
          snapshotObjective: objective,
          snapshotClinicData: clinicSnapshot,
          showFunnelAndGoals,
          snapshotLeadGoals: goalsSnapshot,
          snapshotLeadGoal: showFunnelAndGoals ? leadGoal : null,
          snapshotLeadGoalAchieved: showFunnelAndGoals ? leadGoalAchieved : 0,
        })
      });
      setShareUrl(resp.data?.shareUrl);
    } catch {
      setError('Erro ao gerar link de compartilhamento');
    }
  };

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const el = reportRef.current;
      const orig = { width: el.style.width, maxWidth: el.style.maxWidth };
      el.style.width = '1920px';
      el.style.maxWidth = '1920px';
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, windowWidth: 1920 });
      el.style.width = orig.width;
      el.style.maxWidth = orig.maxWidth;
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `relatorio_combinado_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleCopyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = sourceFilter === 'ALL' ? data : data.filter((r) => r.source === sourceFilter);
  const sorted = [...filtered].sort((a, b) => {
    const v = (x) => Number(x[sortField] || 0);
    return sortDir === 'desc' ? v(b) - v(a) : v(a) - v(b);
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="material-symbols-outlined text-[14px] opacity-30">unfold_more</span>;
    return <span className="material-symbols-outlined text-[14px] text-primary">{sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}</span>;
  };

  return (
    <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
      {/* Header */}
      <header className="mb-12 flex justify-between items-center">
        <div>
          <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-4 flex items-center gap-3">
            <span className="material-symbols-outlined text-primary" style={{ fontSize: 'inherit' }}>merge</span>
            Relatórios Combinados
          </h1>
          <p className="text-secondary max-w-2xl font-body leading-relaxed text-lg">
            Visão unificada de Meta + Google Ads por período e objetivo
          </p>
          {isVisualizing && reportPeriod.startDate && (
            <div className="flex items-center gap-2 mt-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-bold">
                <span className="material-symbols-outlined text-[16px]">calendar_month</span>
                {fmtDate(reportPeriod.startDate)} — {fmtDate(reportPeriod.endDate)}
              </span>
              {objective !== 'ALL' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm font-bold">
                  <span className="material-symbols-outlined text-[16px]">target</span>
                  {OBJECTIVE_OPTIONS.find(o => o.value === objective)?.label || objective}
                </span>
              )}
            </div>
          )}
        </div>
        {isVisualizing && (
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <button onClick={handleShare} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-container hover:bg-surface-container-high transition-colors text-sm font-bold text-on-surface">
              <span className="material-symbols-outlined text-[18px]">share</span>
              Compartilhar
            </button>
            {shareUrl && (
              <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2 flex items-center gap-2 max-w-[220px] overflow-hidden">
                <span className="material-symbols-outlined text-green-600 text-sm shrink-0">link</span>
                <span className="text-green-700 text-xs truncate">{shareUrl}</span>
                <button onClick={handleCopyShare} className="text-green-600 hover:text-green-800 text-xs font-bold whitespace-nowrap">
                  {shareCopied ? '✓' : 'Copiar'}
                </button>
              </div>
            )}
            <button onClick={handleExportPng} disabled={exporting || goalsStatus === 'loading' || goalsStatus === 'error'} title={goalsStatus === 'loading' ? 'Aguarde as metas carregarem' : goalsStatus === 'error' ? 'Metas indisponíveis: tente novamente antes de exportar' : undefined} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors text-sm font-bold disabled:opacity-50">
              <span className="material-symbols-outlined text-[18px]">{exporting ? 'hourglass_empty' : 'image'}</span>
              {exporting ? 'Exportando...' : 'Exportar PNG'}
            </button>
            <button
              onClick={() => setIsVisualizing(false)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-surface-container hover:bg-surface-container-high transition-colors text-sm font-bold text-on-surface"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
              Editar Filtros
            </button>
          </div>
        )}
      </header>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-8 animate-fade-in">
          <span className="material-symbols-outlined text-red-500">error</span>
          <p className="text-red-700 text-sm font-bold">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      {/* Loading progress panel */}
      {loading && loadingAccounts.length > 0 && (
        <div className="mb-8 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.04)] p-6 animate-fade-in">
          <div className="flex items-center gap-3 mb-5">
            <span className="material-symbols-outlined animate-spin text-primary text-[20px]">refresh</span>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Consultando Contas...</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loadingAccounts.map((acc) => {
              const isGoogle = acc.source === 'google';
              return (
                <div key={`${acc.source}-${acc.accountId}`} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isGoogle ? 'border-green-100 bg-green-50/40' : 'border-blue-100 bg-blue-50/40'}`}>
                  <span className={`w-2 h-2 rounded-full animate-pulse shrink-0 ${isGoogle ? 'bg-green-400' : 'bg-blue-400'}`}></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-slate-600 truncate">{acc.accountName}</p>
                    <p className={`text-[9px] font-bold uppercase tracking-wider ${isGoogle ? 'text-green-500' : 'text-blue-500'}`}>{isGoogle ? 'Google Ads' : 'Meta Ads'}</p>
                  </div>
                  <span className="material-symbols-outlined text-slate-300 text-[16px] animate-spin shrink-0">progress_activity</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Main Form Section */}
        {!isVisualizing && (
        <div className="xl:col-span-12 bg-surface-container-lowest rounded-xl p-6 lg:p-10 shadow-[0_8px_32px_rgba(205,25,249,0.04)] animate-in slide-in-from-top-4 fade-in duration-300">
          <div className="space-y-10">
            {/* Date & Objective */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Período de Análise</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {DATE_PRESETS.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => setDatePreset(d.value)}
                      className={`py-3 px-2 font-medium rounded-xl text-[13px] transition-all whitespace-nowrap ${datePreset === d.value ? 'bg-primary/10 text-primary font-bold border border-primary/20' : 'bg-surface-container-low text-on-surface-variant hover:bg-slate-200 border border-transparent'}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                {datePreset === 'custom' && (
                  <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-surface-container-low rounded-xl animate-fade-in">
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Data de Início</label>
                      <input type="date" className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Data de Fim</label>
                      <input type="date" className="w-full bg-white appearance-none px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label">Objetivo do Relatório</label>
                <div className="relative group">
                  <select 
                    className="w-full bg-surface-container-low appearance-none px-5 py-4 rounded-xl border-none focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer"
                    value={objective} 
                    onChange={(e) => setObjective(e.target.value)}
                  >
                    {OBJECTIVE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">expand_more</span>
                </div>
              </div>
            </div>

            {/* Unidades mapeadas (atalhos) */}
            {units.length > 0 && (
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <div className="flex flex-wrap items-center gap-3">
                  <label htmlFor="unit-search" className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px] text-primary" aria-hidden="true">storefront</span>
                    Unidades mapeadas ({units.length})
                  </label>
                  <input
                    id="unit-search"
                    type="search"
                    value={unitSearch}
                    onChange={(e) => setUnitSearch(e.target.value)}
                    placeholder="Buscar unidade..."
                    className="flex-1 min-w-[180px] max-w-xs bg-surface-container-low px-4 py-2 rounded-full border-none focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  />
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      type="button"
                      disabled={!accountsReady}
                      onClick={() => applyUnitSelection([...new Set([...selectedUnits, ...filteredUnits.map((u) => u.id)])])}
                      className="px-3 py-1.5 rounded-full bg-surface-container hover:bg-surface-container-high text-xs font-bold text-on-surface disabled:opacity-50"
                    >
                      Todas{unitSearch.trim() ? ` (${filteredUnits.length})` : ''}
                    </button>
                    <button
                      type="button"
                      disabled={selectedUnits.length === 0}
                      onClick={() => applyUnitSelection([])}
                      className="px-3 py-1.5 rounded-full bg-surface-container hover:bg-surface-container-high text-xs font-bold text-on-surface disabled:opacity-50"
                    >
                      Limpar
                    </button>
                  </div>
                </div>

                <div role="group" aria-label="Unidades mapeadas" className="flex flex-wrap gap-2 max-h-40 overflow-y-auto custom-scrollbar">
                  {filteredUnits.map((u) => {
                    const isSel = selectedUnits.includes(u.id);
                    const a = unitAccountsMap[u.id] || { meta: [], google: [] };
                    return (
                      <button
                        key={u.id}
                        type="button"
                        aria-pressed={isSel}
                        disabled={!accountsReady}
                        onClick={() => toggleUnit(u.id)}
                        title={`${a.meta.length} conta(s) Meta / ${a.google.length} Google`}
                        className={`px-3.5 py-2 rounded-full text-[13px] border transition-colors disabled:opacity-50 ${isSel ? 'bg-primary/10 text-on-primary-fixed-variant font-bold border-primary/30' : 'bg-surface-container-low text-on-surface-variant font-medium border-transparent hover:bg-slate-200'}`}
                      >
                        {isSel && <span className="material-symbols-outlined text-[14px] align-middle mr-1" aria-hidden="true">check</span>}
                        {u.name}
                      </button>
                    );
                  })}
                  {filteredUnits.length === 0 && <p className="text-sm text-slate-500">Nenhuma unidade encontrada.</p>}
                </div>

                <p className="text-xs font-medium text-on-surface" aria-live="polite">
                  {selectedUnits.length} {selectedUnits.length === 1 ? 'unidade' : 'unidades'} = {unitSummary.meta} {unitSummary.meta === 1 ? 'conta' : 'contas'} Meta / {unitSummary.google} Google
                </p>
              </div>
            )}

            {/* Accounts Selection */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4 ${units.length > 0 ? '' : 'border-t border-slate-100'}`}>
              {/* Meta Accounts */}
              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                  Contas Meta ({selectedMeta.length})
                </label>
                <div className="relative">
                  <div 
                    className="w-full bg-surface-container-low px-5 py-4 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer flex justify-between items-center"
                    onClick={() => setMetaDropdownOpen(!metaDropdownOpen)}
                  >
                    <span className="font-medium text-slate-700 truncate block">
                      {selectedMeta.length === 0 ? 'Selecionar contas Meta...' : `${selectedMeta.length} conta(s) selecionada(s)`}
                    </span>
                    <span className="material-symbols-outlined text-slate-400 pointer-events-none">{metaDropdownOpen ? 'expand_less' : 'expand_more'}</span>
                  </div>
                  {metaDropdownOpen && (
                    <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-slate-100 max-h-72 overflow-y-auto p-2 animate-fade-in custom-scrollbar">
                      {metaAccounts.length === 0 ? (
                        <p className="text-sm text-slate-400 p-4 text-center">Nenhuma conta Meta disponível.</p>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-white pb-2 z-10 space-y-2">
                            <input
                              type="search"
                              aria-label="Buscar conta Meta"
                              value={metaSearch}
                              onChange={(e) => setMetaSearch(e.target.value)}
                              placeholder="Buscar por nome ou ID..."
                              className="w-full bg-surface-container-low px-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                            />
                            <div className="flex items-center gap-2 text-xs font-bold">
                              <button type="button" onClick={() => selectAllIn('meta', filteredMeta.map(metaIdOf))} className="px-2.5 py-1 rounded-full bg-surface-container hover:bg-surface-container-high text-on-surface">
                                Selecionar todos{metaSearch.trim() ? ` (${filteredMeta.length})` : ''}
                              </button>
                              <button type="button" onClick={() => clearIn('meta')} className="px-2.5 py-1 rounded-full bg-surface-container hover:bg-surface-container-high text-on-surface">
                                Limpar
                              </button>
                              <span className="ml-auto text-xs text-slate-500">{selectedMeta.length}/{metaAccounts.length}</span>
                            </div>
                          </div>
                          {filteredMeta.length === 0 && <p className="text-sm text-slate-500 p-4 text-center">Nenhuma conta encontrada.</p>}
                          {filteredMeta.map(acc => {
                            const id = metaIdOf(acc);
                            const isSelected = selectedMeta.includes(id);
                            return (
                              <label key={id} className={`flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => toggleMeta(id)} className="w-4 h-4 rounded text-blue-600 border-slate-300 focus:ring-blue-500" />
                                <div className="flex flex-col truncate">
                                  <span className={`text-sm font-bold truncate ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>{acc.name}</span>
                                  <span className="text-xs text-slate-500 font-mono">{id}</span>
                                </div>
                                {unitSources.meta[id] && (
                                  <span className="ml-auto shrink-0 text-xs font-bold text-on-primary-fixed-variant bg-primary/10 px-2 py-0.5 rounded-full">via unidade</span>
                                )}
                              </label>
                            )
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Google Ads Accounts */}
              <div className="space-y-3">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant font-label flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                  Contas Google Ads ({selectedGoogle.length})
                </label>
                <div className="relative">
                  <div 
                    className="w-full bg-surface-container-low px-5 py-4 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm cursor-pointer flex justify-between items-center"
                    onClick={() => setGoogleDropdownOpen(!googleDropdownOpen)}
                  >
                    <span className="font-medium text-slate-700 truncate block">
                      {selectedGoogle.length === 0 ? 'Selecionar contas Google...' : `${selectedGoogle.length} conta(s) selecionada(s)`}
                    </span>
                    <span className="material-symbols-outlined text-slate-400 pointer-events-none">{googleDropdownOpen ? 'expand_less' : 'expand_more'}</span>
                  </div>
                  {googleDropdownOpen && (
                    <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-slate-100 max-h-72 overflow-y-auto p-2 animate-fade-in custom-scrollbar">
                      {googleAccounts.length === 0 ? (
                        <p className="text-sm text-slate-400 p-4 text-center">Nenhuma conta Google Ads conectada.</p>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-white pb-2 z-10 space-y-2">
                            <input
                              type="search"
                              aria-label="Buscar conta Google Ads"
                              value={googleSearch}
                              onChange={(e) => setGoogleSearch(e.target.value)}
                              placeholder="Buscar por nome ou ID..."
                              className="w-full bg-surface-container-low px-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                            />
                            <div className="flex items-center gap-2 text-xs font-bold">
                              <button type="button" onClick={() => selectAllIn('google', filteredGoogle.map((a) => a.customerId))} className="px-2.5 py-1 rounded-full bg-surface-container hover:bg-surface-container-high text-on-surface">
                                Selecionar todos{googleSearch.trim() ? ` (${filteredGoogle.length})` : ''}
                              </button>
                              <button type="button" onClick={() => clearIn('google')} className="px-2.5 py-1 rounded-full bg-surface-container hover:bg-surface-container-high text-on-surface">
                                Limpar
                              </button>
                              <span className="ml-auto text-xs text-slate-500">{selectedGoogle.length}/{googleAccounts.filter(a => !a.isManager).length}</span>
                            </div>
                          </div>
                          {filteredGoogle.length === 0 && <p className="text-sm text-slate-500 p-4 text-center">Nenhuma conta encontrada.</p>}
                          {filteredGoogle.map(acc => {
                            const isSelected = selectedGoogle.includes(acc.customerId);
                            return (
                              <label key={acc.id} className={`flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-green-50/50' : ''}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => toggleGoogle(acc.customerId)} className="w-4 h-4 rounded text-green-600 border-slate-300 focus:ring-green-500" />
                                <div className="flex flex-col truncate">
                                  <span className={`text-sm font-bold truncate ${isSelected ? 'text-green-700' : 'text-slate-700'}`}>{acc.name}</span>
                                  <span className="text-xs text-slate-500 font-mono">{acc.customerId} · {acc.currency}</span>
                                </div>
                                {unitSources.google[acc.customerId] && (
                                  <span className="ml-auto shrink-0 text-xs font-bold text-on-primary-fixed-variant bg-primary/10 px-2 py-0.5 rounded-full">via unidade</span>
                                )}
                              </label>
                            )
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
              <label className="flex items-center gap-3 cursor-pointer select-none text-sm font-bold text-on-surface bg-surface-container-low px-5 py-3.5 rounded-xl border border-outline-variant/30 w-full md:w-auto">
                <input
                  type="checkbox"
                  checked={showFunnelAndGoals}
                  onChange={(e) => setShowFunnelAndGoals(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                />
                <span>Exibir dados de meta e funil planejado</span>
              </label>

              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full md:w-auto md:ml-auto primary-pill-gradient text-white px-8 py-4 rounded-full font-headline text-lg font-extrabold shadow-[0_8px_24px_rgba(205,25,249,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:hover:scale-100"
              >
                {loading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">refresh</span>
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <span>Gerar Relatório</span>
                    <span className="material-symbols-outlined">analytics</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Results */}
      {isVisualizing && summary && (
        <div ref={reportRef} className="mt-12 space-y-8 animate-in slide-in-from-bottom-8 fade-in duration-500">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {[
              { label: 'Investimento Total', value: fmtCurrency(summary.totalSpend), icon: 'payments', color: 'text-blue-500', bg: 'bg-blue-50' },
              { label: 'Impressões', value: Number(summary.totalImpressions).toLocaleString('pt-BR'), icon: 'visibility', color: 'text-indigo-500', bg: 'bg-indigo-50' },
              { label: 'Cliques', value: Number(summary.totalClicks).toLocaleString('pt-BR'), icon: 'ads_click', color: 'text-violet-500', bg: 'bg-violet-50' },
              { label: 'CTR Médio', value: fmtPct(summary.avgCtr), icon: 'percent', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50' },
              { label: 'CPC Médio', value: fmtCurrency(summary.avgCpc), icon: 'monetization_on', color: 'text-rose-500', bg: 'bg-rose-50' },
              { label: objective === 'MESSAGES' ? 'Conversas Iniciadas' : 'Conversões', value: fmt(summary.totalConversions, 1), icon: objective === 'MESSAGES' ? 'chat' : 'check_circle', color: 'text-emerald-500', bg: 'bg-emerald-50' },
            ].map((kpi, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)] flex flex-col items-center justify-center text-center transition-transform hover:-translate-y-1">
                <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                  <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
                </div>
                <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{kpi.value}</h4>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label mt-1">{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* Metas do mês (entra no PNG) */}
          {showFunnelAndGoals && (
            <LeadGoalsBlock
              data={goalsData}
              status={goalsStatus === 'idle' ? 'ready' : goalsStatus}
              type={goalsType}
              onTypeChange={handleGoalsTypeChange}
              onRetry={() => loadGoals(goalsType, goalsUnitIdsRef.current)}
              unmappedCount={goalsUnmapped}
              incompleteCount={goalsIncomplete}
              expectedUnits={goalsExpected}
              overLimitCount={goalsOverLimit}
            />
          )}

          {/* Dados das Clínicas */}
          {selectedMeta.length > 0 && (
            <ClinicDataSection
              adAccountIds={selectedMeta.map(id =>
                metaAccounts.find(a => (a.account_id || a.metaAccountId || a.id?.replace('act_', '')) === id)?.id
              ).filter(Boolean)}
              startDate={getDateRange().startDate}
              endDate={getDateRange().endDate}
              campaignSpend={summary?.totalSpend}
            />
          )}

          {/* ── Accounts Status ── */}
          {accountsStatus.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)] p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-primary text-[18px]">manage_accounts</span>
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Status das Contas Consultadas</h3>
                <span className="ml-auto text-[11px] font-bold text-slate-400">
                  {accountsStatus.filter(s => s.status === 'success').length}/{accountsStatus.length} ok
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {accountsStatus.map((acc) => {
                  const isGoogle = acc.source === 'google';
                  const ok = acc.status === 'success';
                  return (
                    <div
                      key={`${acc.source}-${acc.accountId}`}
                      className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${ok ? (isGoogle ? 'border-green-100 bg-green-50/40' : 'border-blue-100 bg-blue-50/40') : 'border-red-100 bg-red-50/40'}`}
                    >
                      <span className={`material-symbols-outlined text-[18px] shrink-0 mt-0.5 ${ok ? (isGoogle ? 'text-green-500' : 'text-blue-500') : 'text-red-400'}`}>
                        {ok ? 'check_circle' : 'error'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold text-slate-700 truncate">{acc.accountName}</p>
                        <p className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 ${isGoogle ? 'text-green-500' : 'text-blue-500'}`}>
                          {isGoogle ? 'Google Ads' : 'Meta Ads'}
                        </p>
                        {ok ? (
                          <p className="text-[10px] text-slate-400">{acc.campaigns} campanha{acc.campaigns !== 1 ? 's' : ''}</p>
                        ) : (
                          <p className="text-[10px] text-red-400 truncate" title={acc.error}>{acc.error}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Top Campaigns Ranking ── */}
          {data.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="font-headline text-xl font-bold text-slate-800 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[22px]">emoji_events</span>
                  Ranking de Campanhas
                </h3>
                <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-xl">
                  {[
                    { key: 'conversions', label: objective === 'MESSAGES' ? 'Conversas' : 'Conversões' },
                    { key: 'spend', label: 'Investimento' },
                    { key: 'clicks', label: 'Cliques' },
                    { key: 'impressions', label: 'Impressões' },
                  ].map(m => (
                    <button
                      key={m.key}
                      onClick={() => setRankMetric(m.key)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-all ${rankMetric === m.key ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="divide-y divide-slate-50">
                {[...data]
                  .sort((a, b) => Number(b[rankMetric] || 0) - Number(a[rankMetric] || 0))
                  .slice(0, 10)
                  .map((row, i) => {
                    const cfg = SOURCE_CONFIG[row.source] || SOURCE_CONFIG.meta;
                    const maxVal = Math.max(...data.map(r => Number(r[rankMetric] || 0)));
                    const pct = maxVal > 0 ? (Number(row[rankMetric] || 0) / maxVal) * 100 : 0;
                    const displayVal = rankMetric === 'spend'
                      ? fmtCurrency(row[rankMetric])
                      : rankMetric === 'conversions'
                        ? fmt(row[rankMetric], 1)
                        : Number(row[rankMetric] || 0).toLocaleString('pt-BR');
                    return (
                      <div key={i} className={`flex items-center gap-4 px-6 py-4 hover:bg-slate-50/60 transition-colors ${i === 0 ? 'bg-primary/[0.03]' : ''}`}>
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${i === 0 ? 'bg-primary text-white' : i === 1 ? 'bg-slate-200 text-slate-700' : i === 2 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                          {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${cfg.color} shrink-0`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                              {cfg.label}
                            </span>
                            <p className="text-xs text-slate-400 truncate">{row.accountName}</p>
                          </div>
                          <p className="text-sm font-bold text-slate-700 truncate">{row.campaignName}</p>
                          <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-base font-black text-slate-800">{displayVal}</p>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">{rankMetric === 'spend' ? 'Invest.' : rankMetric === 'conversions' ? (objective === 'MESSAGES' ? 'Conversas' : 'Conv.') : rankMetric === 'clicks' ? 'Cliques' : 'Impressões'}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ── Top Criativos ── */}
          {selectedMeta.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="font-headline text-xl font-bold text-slate-800 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[22px]">auto_awesome</span>
                  Top Criativos
                </h3>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block"></span>
                  Meta Ads
                </span>
                {creativesLoading && <span className="material-symbols-outlined animate-spin text-slate-400 text-[18px]">refresh</span>}
              </div>

              {!creativesLoading && topCreatives.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {topCreatives.map((ad, i) => (
                    <div key={ad.adId || i} className={`bg-white rounded-2xl border shadow-[0_4px_16px_rgba(0,0,0,0.04)] overflow-hidden flex flex-col group hover:-translate-y-1 transition-transform ${i === 0 ? 'border-primary/30 ring-1 ring-primary/20' : 'border-slate-100'}`}>
                      {/* Thumbnail */}
                      <div className="aspect-square bg-slate-100 relative overflow-hidden">
                        {ad.thumbnailUrl || ad.imageUrl ? (
                          <img
                            src={ad.thumbnailUrl || ad.imageUrl}
                            alt={ad.adName}
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="material-symbols-outlined text-slate-300 text-4xl">image</span>
                          </div>
                        )}
                        {i < 3 && (
                          <div className={`absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${i === 0 ? 'bg-primary text-white' : i === 1 ? 'bg-slate-600 text-white' : 'bg-amber-500 text-white'}`}>
                            {i + 1}
                          </div>
                        )}
                        {ad.previewUrl && (
                          <a
                            href={ad.previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                          >
                            <span className="material-symbols-outlined text-white text-2xl drop-shadow">open_in_new</span>
                          </a>
                        )}
                      </div>
                      {/* Metrics */}
                      <div className="p-3 flex-1 flex flex-col gap-1">
                        <div className="flex items-start gap-1">
                          <p className="text-[11px] font-bold text-slate-700 line-clamp-2 leading-tight flex-1">{ad.adName}</p>
                          {ad.previewUrl && (
                            <a href={ad.previewUrl} target="_blank" rel="noopener noreferrer"
                              className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md bg-slate-100 hover:bg-primary hover:text-white text-slate-400 transition-colors mt-0.5"
                              title="Abrir criativo">
                              <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                            </a>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 truncate">{ad.accountName}</p>
                        <div className="mt-auto pt-2 grid grid-cols-2 gap-1">
                          <div className="text-center bg-primary/5 rounded-lg p-1.5">
                            <p className="text-[11px] font-black text-primary">{fmt(ad.conversions, 1)}</p>
                            <p className="text-[8px] text-slate-400 uppercase tracking-wider">Conv.</p>
                          </div>
                          <div className="text-center bg-slate-50 rounded-lg p-1.5">
                            <p className="text-[11px] font-black text-slate-700">{fmtCurrency(ad.spend)}</p>
                            <p className="text-[8px] text-slate-400 uppercase tracking-wider">Invest.</p>
                          </div>
                          <div className="text-center bg-slate-50 rounded-lg p-1.5">
                            <p className="text-[11px] font-bold text-slate-600">{ad.conversions > 0 ? fmtCurrency(ad.spend / ad.conversions) : '—'}</p>
                            <p className="text-[8px] text-slate-400 uppercase tracking-wider">{objective === 'MESSAGES' ? 'C/Conv.' : objective === 'LEAD_GENERATION' ? 'CPL' : 'CPA'}</p>
                          </div>
                          <div className="text-center bg-slate-50 rounded-lg p-1.5">
                            <p className="text-[11px] font-bold text-slate-600">{fmtPct(ad.ctr)}</p>
                            <p className="text-[8px] text-slate-400 uppercase tracking-wider">CTR</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!creativesLoading && topCreatives.length === 0 && (
                <div className="bg-slate-50 rounded-2xl p-8 text-center text-slate-400">
                  <span className="material-symbols-outlined text-3xl mb-2 block opacity-40">image_not_supported</span>
                  <p className="text-sm">Nenhum criativo encontrado para o período selecionado.</p>
                </div>
              )}
            </div>
          )}

          {/* Demographics Section */}
          {selectedMeta.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="font-headline text-xl font-bold text-slate-800">Análise Demográfica</h3>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block"></span>
                  Meta Ads
                </span>
                {demoLoading && <span className="material-symbols-outlined animate-spin text-slate-400 text-[18px]">refresh</span>}
              </div>

              {demoError && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <span className="material-symbols-outlined text-amber-500 text-sm">warning</span>
                  <p className="text-amber-700 text-sm">{demoError}</p>
                </div>
              )}

              {demographics && !demoLoading && (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  {/* Age Chart */}
                  <div className="xl:col-span-7 bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)]">
                    <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-[18px]">bar_chart</span>
                      Distribuição por Faixa Etária
                    </h4>
                    <div style={{ height: '240px' }}>
                      <canvas ref={ageChartRef}></canvas>
                    </div>
                  </div>

                  {/* Gender Chart */}
                  <div className="xl:col-span-5 bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)]">
                    <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary text-[18px]">donut_large</span>
                      Conversões por Gênero
                    </h4>
                    <div style={{ height: '240px' }}>
                      <canvas ref={genderChartRef}></canvas>
                    </div>
                  </div>

                  {/* Top Audiences Table */}
                  {demographics.topAudiences && demographics.topAudiences.length > 0 && (
                    <div className="xl:col-span-12 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_16px_rgba(0,0,0,0.02)] overflow-hidden">
                      <div className="p-5 border-b border-slate-100">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary text-[18px]">group</span>
                          Top Públicos que Mais Convertem
                        </h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          <thead className="bg-slate-50/50">
                            <tr>
                              {['#', 'Faixa Etária', 'Gênero', 'Conversões', 'Cliques', 'Investimento', 'Conv/Clique'].map(h => (
                                <th key={h} className="py-3 px-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {demographics.topAudiences.map((r, i) => {
                              const GENDER_LABELS = { male: 'Masculino', female: 'Feminino', unknown: 'Não informado' };
                              const convRate = r.clicks > 0 ? ((r.conversions / r.clicks) * 100).toFixed(1) : '—';
                              return (
                                <tr key={i} className={`hover:bg-slate-50/80 transition-colors ${i === 0 ? 'bg-primary/5' : ''}`}>
                                  <td className="py-3 px-5">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${i === 0 ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                                  </td>
                                  <td className="py-3 px-5 font-bold text-slate-700">{r.age}</td>
                                  <td className="py-3 px-5 text-slate-600">{GENDER_LABELS[r.gender] || r.gender}</td>
                                  <td className="py-3 px-5 font-black text-primary">{fmt(r.conversions, 1)}</td>
                                  <td className="py-3 px-5 text-slate-600">{Number(r.clicks).toLocaleString('pt-BR')}</td>
                                  <td className="py-3 px-5 font-bold text-slate-800">{fmtCurrency(r.spend)}</td>
                                  <td className="py-3 px-5 text-slate-600">{convRate !== '—' ? `${convRate}%` : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {demographics.topAudiences && demographics.topAudiences.length === 0 && !demoLoading && (
                    <div className="xl:col-span-12 bg-slate-50 rounded-2xl p-8 text-center text-slate-400">
                      <span className="material-symbols-outlined text-3xl mb-2 block opacity-40">group_off</span>
                      <p className="text-sm">Nenhum dado demográfico de conversão encontrado para o período selecionado.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.02)] border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 className="font-headline text-xl font-bold text-slate-800">Detalhamento por Campanha</h3>
              
              {/* Source filter */}
              <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-xl">
                {['ALL', 'meta', 'google'].map((src) => (
                  <button
                    key={src}
                    onClick={() => setSourceFilter(src)}
                    className={`px-4 py-2 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-all ${
                      sourceFilter === src ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    {src === 'ALL' ? 'Todas Origens' : src === 'meta' ? 'Meta Ads' : 'Google Ads'}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50/50">
                  <tr>
                    <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label">Origem</th>
                    <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label">Conta</th>
                    <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label">Campanha</th>
                    {[
                      { key: 'spend', label: 'Investimento' },
                      { key: 'impressions', label: 'Impressões' },
                      { key: 'clicks', label: 'Cliques' },
                      { key: 'conversions', label: objective === 'MESSAGES' ? 'Conversas Iniciadas' : 'Conversões' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest font-label text-right cursor-pointer hover:bg-slate-100 select-none group transition-colors"
                        onClick={() => handleSort(col.key)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {col.label} 
                          <span className={`material-symbols-outlined text-[14px] transition-opacity ${sortField === col.key ? 'text-primary opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
                            {sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sorted.map((row, i) => {
                    const cfg = SOURCE_CONFIG[row.source] || SOURCE_CONFIG.meta;
                    return (
                      <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                            {cfg.label}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                            <p className="font-bold text-slate-700 text-xs truncate max-w-[150px]">{row.accountName || row.accountId}</p>
                        </td>
                        <td className="py-4 px-6">
                           <p className="font-medium text-slate-600 truncate max-w-[250px]">{row.campaignName}</p>
                        </td>
                        <td className="py-4 px-6 text-right font-bold text-slate-800">{fmtCurrency(row.spend)}</td>
                        <td className="py-4 px-6 text-right font-medium text-slate-600">{Number(row.impressions || 0).toLocaleString('pt-BR')}</td>
                        <td className="py-4 px-6 text-right font-medium text-slate-600">{Number(row.clicks || 0).toLocaleString('pt-BR')}</td>
                        <td className="py-4 px-6 text-right font-bold text-primary">{fmt(row.conversions, 1)}</td>
                      </tr>
                    );
                  })}
                  {sorted.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="py-16 text-center text-slate-400 font-medium">
                        <span className="material-symbols-outlined text-4xl mb-3 opacity-20 block">inbox</span>
                        <p className="text-sm font-bold text-slate-500 mb-1">Nenhuma campanha encontrada</p>
                        {accountsStatus.length > 0 && accountsStatus.every(s => s.campaigns === 0 && s.status === 'success') ? (
                          <p className="text-xs text-slate-400">As contas foram consultadas mas não há campanhas com entrega no período selecionado.</p>
                        ) : accountsStatus.some(s => s.status === 'error') ? (
                          <p className="text-xs text-red-400">Algumas contas retornaram erro — verifique o status das contas acima.</p>
                        ) : sourceFilter !== 'ALL' ? (
                          <p className="text-xs text-slate-400">Tente selecionar "Todas Origens" no filtro acima.</p>
                        ) : (
                          <p className="text-xs text-slate-400">Verifique o período e objetivo selecionados.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
